import { Component, OnInit, ViewChild } from '@angular/core';
import { TableConfig } from "@app/shared/util/table-config.utils";
import { Reason } from "@models/reason.model";
import { ReasonsService } from "@services/reasons.service";
import { ToastsManager } from "@app/shared/util/toastr.service";
import { TranslateParser, TranslateService } from "@ngx-translate/core";
import { ReasonsCreateModalComponent } from "@app/roles/admin/pages/issues/components/reasons/components";
import { Log } from "ng2-logger";
import { PermissionService } from "@app/authentication/services/permission.service";

@Component({
    selector: 'app-reasons',
    templateUrl: './reasons.component.html',
    styleUrls: ['./reasons.component.scss']
})
export class ReasonsComponent implements OnInit {
    @ViewChild(ReasonsCreateModalComponent, {static: true}) reasonFormModal: ReasonsCreateModalComponent;

    _logger = Log.create(ReasonsComponent.name);
    reasons: Reason[] = [];
    tableConfig = new TableConfig();
    isLoadingReasons = false;

    constructor(private reasonsService: ReasonsService,
                private toastr: ToastsManager,
                private permissionService: PermissionService,
                private translationService: TranslateService,
                private translateParser: TranslateParser) {

    }

    ngOnInit() {
        this.getAllReasons();
    }

    openReasonModal() {
        this.reasonFormModal.open();
    }

    onTableRowClick(reason: Reason) {
        this.reasonFormModal.open(reason);
    }

    getAllReasons(tableConfig: TableConfig = this.tableConfig) {
        this.isLoadingReasons = true;
        this.reasonsService.getAllIReasons(tableConfig).subscribe(
            (data: any) => {
                this.reasons = data.items;
                this.tableConfig.pager.count = data.count;
                this.isLoadingReasons = false;
            },
            (error: any) => {
                this._logger.error(error);
            }
        );
    }

    createReason(reason: Reason) {
        this.isLoadingReasons = true;
        this.reasonsService.createReason(reason).subscribe(
            (data: any) => {
                this.getAllReasons();
                this.toastr.success(this.buildCreateMessage(data.name));
                this.isLoadingReasons = false;
                this.reasonFormModal.close();
            },
            (error: any) => {
                this._logger.error(error);
                this.isLoadingReasons = false;
            }
        );
    }

    deleteReason(reason: Reason) {
        this.isLoadingReasons = true;
        this.reasonsService.deleteReason(reason).subscribe(
            (data: any) => {
                this.getAllReasons();
                this.toastr.success(this.buildDeleteMessage(reason.name));
                this.isLoadingReasons = false;
                this.reasonFormModal.close();
            },
            (error: any) => {
                this._logger.error(error);
                this.isLoadingReasons = false;
            }
        );
    }

    updateReason(reasonUpdate) {
        this.isLoadingReasons = true;
        this.reasonsService.updateReason(reasonUpdate.newReason).subscribe(
            (data: any) => {
                this.getAllReasons();
                this.toastr.success(this.buildUpdateMessage(reasonUpdate.previousReasonName));
                this.isLoadingReasons = false;
                this.reasonFormModal.close();
            },
            (error: any) => {
                this._logger.error(error);
                this.isLoadingReasons = false;
            }
        );
    }

    onSortChange(tableConfig: TableConfig) {
        this.getAllReasons(tableConfig);
    }

    onFilter(tableConfig: TableConfig) {
        this.getAllReasons(tableConfig);
    }

    onPageChange(tableConfig: TableConfig) {
        this.getAllReasons(tableConfig);
    }

    buildCreateMessage(reasonName) {
        const message = this.translationService.instant('admin.reasons.toasts.created');
        return this.translateParser.interpolate(message, {reasonName: reasonName});
    }

    buildUpdateMessage(reasonName) {
        const message = this.translationService.instant('admin.reasons.toasts.updated');
        return this.translateParser.interpolate(message, {reasonName: reasonName});
    }

    buildDeleteMessage(reasonName) {
        const message = this.translationService.instant('admin.reasons.toasts.deleted');
        return this.translateParser.interpolate(message, {reasonName: reasonName});
    }

    shouldRenderModifiers() {
        return this.permissionService.hasHasPermissionForFeature('MANAGE_REASONS');
    }
}
